import { useEffect, useId, useState } from 'react'
import { PART_REQUEST_TYPES } from '../data/constants'
import { fetchPartsPage } from '../services/inventoryApi'
import { validatePurchaseInput } from '../utils/partRequestPurchase'
import { formatCurrentStock, partTitle } from '../utils/stock'
import QuotationField from './QuotationField.jsx'
import UnitSelect from './UnitSelect.jsx'

const EMPTY_NEW = {
  itemName: '',
  manufacturer: '',
  model: '',
  specification: '',
  requestedQty: 1,
  unit: '개',
  reason: '',
}

export default function PartRequestModal({ open, onClose, onSubmit }) {
  const titleId = useId()
  const [requestType, setRequestType] = useState('EXISTING_PART')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedPart, setSelectedPart] = useState(null)
  const [existingQty, setExistingQty] = useState(1)
  const [existingReason, setExistingReason] = useState('')
  const [newForm, setNewForm] = useState(EMPTY_NEW)
  const [purchaseUrl, setPurchaseUrl] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [quotationFile, setQuotationFile] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setRequestType('EXISTING_PART')
    setQuery('')
    setResults([])
    setSelectedPart(null)
    setExistingQty(1)
    setExistingReason('')
    setNewForm(EMPTY_NEW)
    setPurchaseUrl('')
    setUnitPrice('')
    setQuotationFile(null)
    setError('')
    setSaving(false)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || requestType !== 'EXISTING_PART') return undefined
    const q = query.trim()
    if (!q) {
      setResults([])
      setSearching(false)
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const result = await fetchPartsPage({ q, page: 1, limit: 20 })
        if (cancelled) return
        setResults(result.items.filter((part) => !part.mergedIntoPartId))
      } catch (err) {
        if (!cancelled) setError(err.message || '품목을 검색하지 못했습니다.')
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query, requestType])

  if (!open) return null

  async function handleSubmit(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      if (requestType === 'EXISTING_PART') {
        if (!selectedPart) {
          setError('기존 재고 품목을 선택해 주세요.')
          setSaving(false)
          return
        }
        const quantity = Number(existingQty)
        if (!Number.isInteger(quantity) || quantity <= 0) {
          setError('요청수량은 1 이상이어야 합니다.')
          setSaving(false)
          return
        }
        if (!existingReason.trim()) {
          setError('요청사유를 입력해 주세요.')
          setSaving(false)
          return
        }
        const purchaseError = validatePurchaseInput({ purchaseUrl, unitPrice, file: quotationFile })
        if (purchaseError) {
          setError(purchaseError)
          setSaving(false)
          return
        }
        await onSubmit({
          requestType: 'EXISTING_PART',
          partId: selectedPart.id,
          requestedQty: quantity,
          unit: selectedPart.unit || '개',
          reason: existingReason.trim(),
          purchaseUrl: purchaseUrl.trim(),
          unitPrice: String(unitPrice).trim().replace(/,/g, ''),
        }, quotationFile)
      } else {
        if (!newForm.itemName.trim()) {
          setError('품명을 입력해 주세요.')
          setSaving(false)
          return
        }
        const quantity = Number(newForm.requestedQty)
        if (!Number.isInteger(quantity) || quantity <= 0) {
          setError('요청수량은 1 이상이어야 합니다.')
          setSaving(false)
          return
        }
        if (!newForm.reason.trim()) {
          setError('요청사유를 입력해 주세요.')
          setSaving(false)
          return
        }
        const purchaseError = validatePurchaseInput({ purchaseUrl, unitPrice, file: quotationFile })
        if (purchaseError) {
          setError(purchaseError)
          setSaving(false)
          return
        }
        await onSubmit({
          requestType: 'NEW_ITEM',
          itemName: newForm.itemName.trim(),
          manufacturer: newForm.manufacturer.trim(),
          model: newForm.model.trim(),
          specification: newForm.specification.trim(),
          requestedQty: quantity,
          unit: newForm.unit.trim() || '개',
          reason: newForm.reason.trim(),
          purchaseUrl: purchaseUrl.trim(),
          unitPrice: String(unitPrice).trim().replace(/,/g, ''),
        }, quotationFile)
      }
    } catch (err) {
      setError(err.message || '요청을 등록하지 못했습니다.')
      setSaving(false)
      return
    }
    setSaving(false)
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form
        className="modal-panel modal-panel--form"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="modal-title">
          필요 품목 요청
        </h2>

        <fieldset className="review-step">
          <legend>요청 구분</legend>
          <div className="review-choice-row" role="radiogroup" aria-label="요청 구분">
            {PART_REQUEST_TYPES.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`review-choice ${requestType === item.value ? 'is-selected' : ''}`}
                aria-pressed={requestType === item.value}
                onClick={() => {
                  setRequestType(item.value)
                  setError('')
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </fieldset>

        {requestType === 'EXISTING_PART' ? (
          <>
            <label className="field">
              <span>기존 재고 품목 검색</span>
              <input
                className="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="모델명, 제조사, 품명, 관리번호"
              />
            </label>
            {searching ? <p className="muted">검색 중...</p> : null}
            {!searching && query.trim() && results.length === 0 ? (
              <p className="empty">조건에 맞는 품목이 없습니다. 재고에 없는 품목으로 요청할 수 있습니다.</p>
            ) : null}
            {results.length > 0 ? (
              <ul className="merge-search-list">
                {results.map((part) => (
                  <li key={part.id}>
                    <button
                      type="button"
                      className={`merge-search-item ${selectedPart?.id === part.id ? 'is-selected' : ''}`}
                      onClick={() => setSelectedPart(part)}
                    >
                      <strong className="model-name">{partTitle(part)}</strong>
                      <span>
                        {part.partCode} · {part.manufacturer} · {part.name || '-'} · {formatCurrentStock(part)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {selectedPart ? (
              <div className="request-selected">
                <p>
                  <span>품목명</span>
                  <strong>{selectedPart.name || selectedPart.model || '-'}</strong>
                </p>
                <p>
                  <span>제조사</span>
                  <strong>{selectedPart.manufacturer || '-'}</strong>
                </p>
                <p>
                  <span>모델명</span>
                  <strong className="model-name">{selectedPart.model || '-'}</strong>
                </p>
                <p>
                  <span>현재 재고수량</span>
                  <strong>{formatCurrentStock(selectedPart)}</strong>
                </p>
              </div>
            ) : null}

            <label className="field">
              <span>요청수량</span>
              <div className="qty-stepper">
                <button
                  type="button"
                  className="btn btn--ghost btn--touch"
                  onClick={() => setExistingQty((n) => Math.max(1, Number(n) - 1))}
                  aria-label="수량 감소"
                >
                  −
                </button>
                <input
                  className="input input--qty"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={existingQty}
                  onChange={(event) => setExistingQty(event.target.value)}
                />
                <button
                  type="button"
                  className="btn btn--ghost btn--touch"
                  onClick={() => setExistingQty((n) => Number(n) + 1)}
                  aria-label="수량 증가"
                >
                  +
                </button>
              </div>
            </label>
            <label className="field">
              <span>요청사유</span>
              <textarea
                className="input input--area"
                value={existingReason}
                onChange={(event) => setExistingReason(event.target.value)}
                placeholder="필요한 이유와 용도를 적어 주세요."
              />
            </label>
          </>
        ) : (
          <>
            <label className="field">
              <span>품명 (필수)</span>
              <input
                className="input"
                value={newForm.itemName}
                onChange={(e) => setNewForm((current) => ({ ...current, itemName: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>제조사</span>
              <input
                className="input"
                value={newForm.manufacturer}
                onChange={(e) => setNewForm((current) => ({ ...current, manufacturer: e.target.value }))}
                placeholder="모르면 비워 두세요"
              />
            </label>
            <label className="field">
              <span>모델명</span>
              <input
                className="input"
                value={newForm.model}
                onChange={(e) => setNewForm((current) => ({ ...current, model: e.target.value }))}
                placeholder="모르면 비워 두세요"
              />
            </label>
            <label className="field">
              <span>사양</span>
              <textarea
                className="input input--area"
                value={newForm.specification}
                onChange={(e) => setNewForm((current) => ({ ...current, specification: e.target.value }))}
                placeholder="전압, 용량 등 아는 범위만 적어 주세요"
              />
            </label>
            <label className="field">
              <span>요청수량 (필수)</span>
              <div className="qty-stepper">
                <button
                  type="button"
                  className="btn btn--ghost btn--touch"
                  onClick={() =>
                    setNewForm((current) => ({
                      ...current,
                      requestedQty: Math.max(1, Number(current.requestedQty) - 1),
                    }))
                  }
                  aria-label="수량 감소"
                >
                  −
                </button>
                <input
                  className="input input--qty"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={newForm.requestedQty}
                  onChange={(event) =>
                    setNewForm((current) => ({ ...current, requestedQty: event.target.value }))
                  }
                />
                <button
                  type="button"
                  className="btn btn--ghost btn--touch"
                  onClick={() =>
                    setNewForm((current) => ({
                      ...current,
                      requestedQty: Number(current.requestedQty) + 1,
                    }))
                  }
                  aria-label="수량 증가"
                >
                  +
                </button>
              </div>
            </label>
            <label className="field">
              <span>단위</span>
              <UnitSelect
                value={newForm.unit}
                onChange={(unit) => setNewForm((current) => ({ ...current, unit }))}
              />
            </label>
            <label className="field">
              <span>요청사유 (필수)</span>
              <textarea
                className="input input--area"
                value={newForm.reason}
                onChange={(e) => setNewForm((current) => ({ ...current, reason: e.target.value }))}
                placeholder="필요한 이유와 용도를 적어 주세요."
              />
            </label>
          </>
        )}

        <label className="field">
          <span>구매처 링크</span>
          <input
            className="input"
            type="url"
            inputMode="url"
            value={purchaseUrl}
            onChange={(event) => setPurchaseUrl(event.target.value)}
            placeholder="https://"
          />
        </label>
        <label className="field">
          <span>예상 단가(원) *</span>
          <input
            className="input"
            inputMode="numeric"
            value={unitPrice}
            onChange={(event) => setUnitPrice(event.target.value)}
            placeholder="125000"
          />
        </label>
        <QuotationField file={quotationFile} onFile={setQuotationFile} />

        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
            {saving ? '등록 중...' : '요청 등록'}
          </button>
        </div>
      </form>
    </div>
  )
}
